import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, Loader2, Music, Disc, ShoppingBag, Volume2, FileMusic, Film, Users } from 'lucide-react';
import axios from 'axios';
import { API, toFullUrl } from '../config/api';

const SECTION_META = {
  tracks: { label: 'Tracks', icon: Music, color: '#DAA520' },
  albums: { label: 'Albums', icon: Disc, color: '#FF3B30' },
  merch: { label: 'Merch', icon: ShoppingBag, color: '#22C55E' },
  beats: { label: 'Beats', icon: Volume2, color: '#A78BFA' },
  mixtapes: { label: 'Mixtapes', icon: FileMusic, color: '#FB923C' },
  free_media: { label: 'Free Media', icon: Music, color: '#22D3EE' },
  videos: { label: 'Videos', icon: Film, color: '#F472B6' },
  featured_artists: { label: 'Featured Artists', icon: Users, color: '#FF3B30' },
};

const SECTION_ORDER = ['featured_artists', 'tracks', 'albums', 'mixtapes', 'beats', 'merch', 'videos', 'free_media'];

/** Compute a /link href for a result item based on its section. */
function hrefForItem(section, item) {
  if (section === 'featured_artists') return `/artist/${item.slug || ''}`;
  if (section === 'merch') return `/store`;
  if (section === 'beats') return `/beats`;
  // Default → scroll to corresponding homepage section
  return `/#${section}`;
}

export function SiteSearch({ compact = false }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  // Debounced fetch
  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/search`, { params: { q: q.trim(), limit: 5 } });
        setResults(data);
      } catch (err) {
        setResults({ results: {}, total: 0 });
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Close on click-outside
  useEffect(() => {
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, []);

  // CMD/CTRL+K shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const orderedSections = results && results.results
    ? SECTION_ORDER.filter(s => results.results[s] && results.results[s].length > 0)
    : [];

  return (
    <div ref={wrapperRef} className="relative" data-testid="site-search">
      <div className={`flex items-center bg-[#141414]/80 backdrop-blur-md border transition-colors ${open ? 'border-[#DAA520]' : 'border-[#262626] hover:border-[#404040]'} ${compact ? 'w-full' : 'w-44 sm:w-64 md:w-80'}`}>
        <Search className="w-3.5 h-3.5 text-[#737373] ml-3 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search tracks, albums, merch…"
          className="flex-1 bg-transparent text-xs px-2 py-2 outline-none text-white placeholder:text-[#525252] font-mono"
          data-testid="site-search-input"
        />
        {q && (
          <button onClick={() => { setQ(''); setResults(null); inputRef.current?.focus(); }}
            className="text-[#525252] hover:text-white p-1 mr-1" data-testid="site-search-clear">
            <X className="w-3 h-3" />
          </button>
        )}
        {!compact && (
          <span className="hidden sm:flex text-[9px] text-[#525252] font-mono mr-2 px-1.5 py-0.5 border border-[#262626] rounded">⌘K</span>
        )}
      </div>

      {/* Results dropdown */}
      {open && q && (
        <div className="absolute right-0 sm:right-auto sm:left-0 top-full mt-2 w-[92vw] sm:w-[480px] max-h-[70vh] overflow-y-auto bg-[#0A0A0A] border border-[#262626] shadow-2xl z-[100]"
          data-testid="site-search-results">
          {loading && (
            <div className="px-4 py-6 text-center text-xs text-[#737373] flex items-center justify-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
            </div>
          )}
          {!loading && results && results.total === 0 && (
            <div className="px-4 py-8 text-center text-xs text-[#737373]">No matches for &ldquo;<span className="text-white">{q}</span>&rdquo;</div>
          )}
          {!loading && orderedSections.map(sec => {
            const meta = SECTION_META[sec];
            const Icon = meta?.icon || Music;
            const items = results.results[sec];
            return (
              <div key={sec} className="border-b border-[#262626] last:border-0">
                <div className="flex items-center justify-between px-3 py-2 bg-[#141414]">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: meta.color }}>
                    <Icon className="w-3 h-3 inline mr-1.5 -mt-0.5" />
                    {meta.label}
                  </p>
                  <span className="text-[9px] text-[#525252] font-mono">{items.length}</span>
                </div>
                <div>
                  {items.map(item => {
                    const title = item.title || item.name || 'Untitled';
                    const subtitle = item.artist || item.producer || item.media_type || item.bio || '';
                    const img = item.cover_image || item.cover_url || item.thumbnail_url || item.photo || item.image_url;
                    return (
                      <Link
                        key={item.id}
                        to={hrefForItem(sec, item)}
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-[#141414] transition-colors border-l-2 border-l-transparent hover:border-l-[#DAA520]"
                        data-testid={`search-result-${sec}-${item.id}`}
                      >
                        {img ? (
                          <img src={toFullUrl(img)} alt="" className="w-9 h-9 object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-9 h-9 bg-[#262626] flex items-center justify-center flex-shrink-0">
                            <Icon className="w-3.5 h-3.5 text-[#666]" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-white truncate">{title}</p>
                          {subtitle && <p className="text-[10px] text-[#737373] truncate">{subtitle}</p>}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {!loading && results && results.total > 0 && (
            <p className="text-[9px] text-[#525252] font-mono px-3 py-2 bg-[#141414] text-center uppercase tracking-wider">
              {results.total} result{results.total === 1 ? '' : 's'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
